import { useNavigate } from 'react-router-dom';
import { Scissors, ArrowLeft, Users, Star, MapPin, Zap } from 'lucide-react';
import Footer from '../components/Footer';
import SEO, { breadcrumbSchema } from '../components/SEO';

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fff5f5, #fff, #fdf4ff)' }}>
      <SEO
        title="About Us"
        description="BookYourSalons is India's premium salon booking platform. Learn about our mission to connect customers with verified, top-rated salons and parlours across India."
        path="/about"
        schema={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'About Us', path: '/about' },
        ])}
      />
      {/* Nav */}
      <div style={{
        background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '12px 20px',
        display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 100,
      }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#374151', fontWeight: 600, fontSize: '0.85rem' }}>
          <ArrowLeft size={18} /> Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ background: 'linear-gradient(135deg, #dc2626, #e11d48)', padding: '6px', borderRadius: '8px' }}>
            <Scissors size={14} color="white" />
          </div>
          <span style={{ fontWeight: 800, fontFamily: "'Syne', sans-serif", fontSize: '0.95rem', color: '#111827' }}>
            BookYourSalons
          </span>
        </div>
      </div>

      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '40px 20px' }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div style={{
            width: '72px', height: '72px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #dc2626, #e11d48)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', boxShadow: '0 8px 32px rgba(220,38,38,0.3)',
          }}>
            <Scissors size={32} color="white" />
          </div>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, fontFamily: "'Syne', sans-serif", color: '#111827', marginBottom: '12px' }}>
            About BookYourSalons
          </h1>
          <p style={{ color: '#6b7280', fontSize: '1rem', lineHeight: 1.7, maxWidth: '500px', margin: '0 auto' }}>
            India's premium salon booking platform — making beauty services accessible, convenient, and reliable for everyone.
          </p>
        </div>

        {/* Mission */}
        <div style={{
          background: 'linear-gradient(135deg, #dc2626, #e11d48)',
          borderRadius: '20px', padding: '28px', marginBottom: '28px', color: 'white',
        }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 800, fontFamily: "'Syne', sans-serif", marginBottom: '12px' }}>🎯 Our Mission</h2>
          <p style={{ lineHeight: 1.7, opacity: 0.92, fontSize: '0.92rem' }}>
            To empower every Indian to look and feel their best by connecting them with top-rated, verified salons in their neighborhood — anytime, anywhere, with zero hassle.
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '28px' }}>
          {[
            { icon: <Users size={20} color="#dc2626" />, num: '500+', label: 'Happy Customers' },
            { icon: <Scissors size={20} color="#7c3aed" />, num: '50+', label: 'Partner Salons' },
            { icon: <Star size={20} color="#f59e0b" />, num: '4.8★', label: 'Average Rating' },
            { icon: <MapPin size={20} color="#059669" />, num: 'PAN India', label: 'Coverage' },
          ].map(({ icon, num, label }, i) => (
            <div key={i} style={{
              background: 'white', borderRadius: '16px', padding: '20px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)', textAlign: 'center',
              border: '1px solid rgba(0,0,0,0.04)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>{icon}</div>
              <p style={{ fontSize: '1.3rem', fontWeight: 800, color: '#111827', marginBottom: '4px' }}>{num}</p>
              <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>{label}</p>
            </div>
          ))}
        </div>

        {/* Why us */}
        <div style={{ background: 'white', borderRadius: '20px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '28px' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 800, fontFamily: "'Syne', sans-serif", color: '#111827', marginBottom: '16px' }}>
            ✨ Why Choose Us?
          </h2>
          {[
            { icon: '⚡', title: 'Instant Booking', desc: 'Book any salon in seconds — no calls, no waiting.' },
            { icon: '✅', title: 'Verified Salons', desc: 'Every salon is manually reviewed and approved.' },
            { icon: '🔒', title: 'Secure & Safe', desc: 'Your data is encrypted and never shared.' },
            { icon: '💰', title: 'No Hidden Charges', desc: 'What you see is what you pay.' },
          ].map(({ icon, title, desc }, i) => (
            <div key={i} style={{ display: 'flex', gap: '14px', marginBottom: i < 3 ? '16px' : 0, paddingBottom: i < 3 ? '16px' : 0, borderBottom: i < 3 ? '1px solid #f3f4f6' : 'none' }}>
              <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{icon}</span>
              <div>
                <p style={{ fontWeight: 700, color: '#111827', fontSize: '0.9rem', marginBottom: '2px' }}>{title}</p>
                <p style={{ color: '#6b7280', fontSize: '0.8rem', lineHeight: 1.5 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Company info */}
        <div style={{ background: '#f9fafb', borderRadius: '16px', padding: '20px', textAlign: 'center', border: '1px solid #f3f4f6' }}>
          <p style={{ color: '#374151', fontWeight: 600, fontSize: '0.85rem', marginBottom: '4px' }}>
            BookYourSalons Private Limited
          </p>
          <p style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
            📧 bookyoursalons@gmail.com &nbsp;|&nbsp; 🇮🇳 India
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutUs;
